/**
 * SupplierInvoiceDetailModal — fatura detayı + İşle/Öde aksiyonları.
 *
 * DRAFT  → "İşle" (post_invoice): cariye INVOICE düşer, GR'ye bağlı satır
 *          varsa İrsaliye borcu mahsuplaşır (B4).
 * POSTED / PARTIALLY_PAID → "Öde" (pay): SupplierPayment + PAYMENT ledger.
 */
import { useState, type ReactNode } from "react";
import {
  Badge, Button, FileInput, Group, Modal, NumberInput, Select, Stack, Table, Text, TextInput,
} from "@mantine/core";
import { DateInput } from "@mantine/dates";
import { IconPaperclip, IconUpload } from "@tabler/icons-react";
import { notifications } from "@mantine/notifications";
import { useQueryClient } from "@tanstack/react-query";
import { apiClient, useAction, useSupplierBalance } from "../../../shared/api";
import { MoneyDisplay, formatDate } from "../../../shared/ui";

const INV_STATUS: Record<string, { label: string; color: string }> = {
  DRAFT: { label: "Taslak", color: "gray" },
  POSTED: { label: "İşlendi", color: "blue" },
  PARTIALLY_PAID: { label: "Kısmen Ödendi", color: "orange" },
  PAID: { label: "Ödendi", color: "green" },
  PARTIALLY_REFUNDED: { label: "Kısmen İade", color: "grape" },
  CANCELLED: { label: "İptal", color: "red" },
};

export function SupplierInvoiceDetailModal({
  invoice, onClose, onChanged,
}: { invoice: any | null; onClose: () => void; onChanged?: () => void }) {
  const qc = useQueryClient();
  const post = useAction("supplier-invoices", "post_invoice");
  const pay = useAction("supplier-invoices", "pay");
  const payInstr = useAction("supplier-invoices", "pay-with-instrument");
  const [payOpen, setPayOpen] = useState(false);
  // Çek/senet ödeme alanları (yöntem CHECK/NOTE seçilince görünür).
  const [serialNo, setSerialNo] = useState("");
  const [dueDate, setDueDate] = useState<Date | null>(null);
  const [bankName, setBankName] = useState("");

  // Fatura İşle/Öde cariyi (bakiye + ekstre) değiştirir. useAction yalnız
  // ["supplier-invoices"]'i invalidate ettiği için bakiye kartı ve ekstre bayat
  // kalıyordu (kart 0'da takılıp F5 ile düzelmesi bu yüzdendi). Prefix-match ile
  // tüm tenant/supplier varyantlarını tazele.
  function refreshLedgerCaches() {
    qc.invalidateQueries({ queryKey: ["supplier-balance"] });
    qc.invalidateQueries({ queryKey: ["supplier-ledger"] });
    qc.invalidateQueries({ queryKey: ["suppliers"] });
    // Çek/senet ödeme → portföy de değişir (Çek/Senet sekmesi).
    qc.invalidateQueries({ queryKey: ["instruments"] });
    // Fatura İşle → PO COMPLETED'a geçebilir (tam teslim+faturalı). PO listesi/
    // detayı bayat kalmasın (kullanıcı "hâlâ Teslim Alındı görünüyor" dedi).
    qc.invalidateQueries({ queryKey: ["purchase-orders"] });
    // GR listesi de "Faturalandı" rozetini göstersin (is_invoiced değişti).
    qc.invalidateQueries({ queryKey: ["goods-receipts"] });
  }
  const [payAmount, setPayAmount] = useState<number | string>(0);
  const [payMethod, setPayMethod] = useState("BANK_TRANSFER");
  const isInstrument = payMethod === "CHECK" || payMethod === "NOTE";

  // --- Kısmi iade (tedarikçiye mal iadesi) ---
  // İade yalnız GR satırına bağlı kalemler için (mal teslim alınmış olmalı).
  const [returnOpen, setReturnOpen] = useState(false);
  const [returnQty, setReturnQty] = useState<Record<string, number | string>>({});
  const [returnReason, setReturnReason] = useState("");
  const [returnSaving, setReturnSaving] = useState(false);

  const [downloading, setDownloading] = useState(false);
  const [uploading, setUploading] = useState(false);
  const lines: any[] = invoice?.lines || [];
  const returnableLines = lines.filter((l) => l.goods_receipt_line);

  async function doReturn() {
    const returnLines = returnableLines
      .map((l) => ({
        goods_receipt_line: l.goods_receipt_line,
        return_quantity: Number(returnQty[l.id] || 0),
        unit_price_minor: l.unit_price_minor,
      }))
      .filter((rl) => rl.return_quantity > 0);
    if (returnLines.length === 0) {
      notifications.show({ message: "En az bir kaleme iade miktarı girin", color: "orange" });
      return;
    }
    setReturnSaving(true);
    try {
      const created = await apiClient.post(`/api/v1/supplier-returns/`, {
        supplier: invoice.supplier,
        invoice: invoice.id,
        reason: returnReason,
        lines: returnLines,
      });
      // DRAFT iade → hemen onayla (stok düş + cari REFUND).
      await apiClient.post(`/api/v1/supplier-returns/${created.data.id}/authorize/`);
      notifications.show({ message: "Kısmi iade işlendi", color: "green" });
      setReturnOpen(false);
      setReturnQty({});
      setReturnReason("");
      refreshLedgerCaches();
      onChanged?.();
    } catch (e: any) {
      notifications.show({ message: e?.response?.data?.detail || "İade başarısız", color: "red" });
    } finally {
      setReturnSaving(false);
    }
  }
  const st = invoice ? INV_STATUS[invoice.status] || { label: invoice.status, color: "gray" } : null;
  const remaining = invoice ? Number(invoice.total_minor || 0) - Number(invoice.paid_amount_minor || 0) : 0;

  // Cari bakiye — "Öde" varsayılanını sınırlamak için. Faturasız (on-account)
  // ödemeler fatura paid_amount'una yazılmaz; o yüzden fatura kalanı (remaining)
  // cari bakiyeden büyük olabilir (örn. 118₺ faturasız ödendi, cari 21.20 ama
  // fatura kalanı 121.20). Varsayılan ödemeyi cari borçla sınırla ki kullanıcı
  // yanlışlıkla zaten ödenmiş tutarı tekrar ödemesin (fazla ödeme).
  const { data: balance } = useSupplierBalance(invoice?.supplier ?? null);
  const supplierDebtMinor = Math.max(0, Number(balance?.balance_minor || 0));
  // Önerilen ödeme: fatura kalanı ile cari borcun küçüğü (>0 ise).
  const suggestedPayMinor = supplierDebtMinor > 0
    ? Math.min(remaining, supplierDebtMinor)
    : remaining;

  async function downloadAttachment() {
    setDownloading(true);
    try {
      // Kısa süreli imzalı (presigned) URL al, yeni sekmede aç.
      const { data } = await apiClient.get(`/api/v1/supplier-invoices/${invoice.id}/attachment-url/`);
      if (data?.url) window.open(data.url, "_blank", "noopener,noreferrer");
      else notifications.show({ message: "Ek bulunamadı", color: "orange" });
    } catch (e: any) {
      notifications.show({ message: e?.response?.data?.detail || "Ek indirilemedi", color: "red" });
    } finally {
      setDownloading(false);
    }
  }

  // Gerçek fatura belgesini SONRADAN MinIO'ya yükle/değiştir (create'te
  // koymasa da buradan ekleyebilir).
  async function uploadAttachment(file: File | null) {
    if (!file) return;
    setUploading(true);
    try {
      const fd = new FormData();
      fd.append("attachment", file);
      await apiClient.post(`/api/v1/supplier-invoices/${invoice.id}/upload-attachment/`, fd);
      qc.invalidateQueries({ queryKey: ["supplier-invoices"] });
      notifications.show({ message: "Fatura belgesi yüklendi", color: "green" });
      onChanged?.();
    } catch (e: any) {
      notifications.show({ message: e?.response?.data?.detail || "Yüklenemedi", color: "red" });
    } finally {
      setUploading(false);
    }
  }

  async function doPost() {
    try {
      await post.mutateAsync({ id: invoice.id });
      refreshLedgerCaches();
      notifications.show({ message: "Fatura işlendi (cariye düştü)", color: "green" });
      onChanged?.(); onClose();
    } catch (e: any) {
      notifications.show({ message: e?.response?.data?.detail || "Hata", color: "red" });
    }
  }

  function isoDate(d: Date): string {
    return `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, "0")}-${String(d.getDate()).padStart(2, "0")}`;
  }

  function resetPay() {
    setPayOpen(false); setPayAmount(0); setPayMethod("BANK_TRANSFER");
    setSerialNo(""); setDueDate(null); setBankName("");
  }

  async function doPay() {
    const minor = Math.round(Number(payAmount) * 100);
    if (minor <= 0) { notifications.show({ message: "Tutar 0'dan büyük olmalı", color: "red" }); return; }
    try {
      if (isInstrument) {
        // Çek/senet ile ödeme → faturayı kapatır, Çek/Senet sekmesinde görünür,
        // cari düşer (tek CHECK_ISSUED ledger; çift sayım yok). PaymentModal deseni.
        if (!serialNo.trim()) { notifications.show({ message: "Seri no girin", color: "orange" }); return; }
        if (!dueDate) { notifications.show({ message: "Vade tarihi girin", color: "orange" }); return; }
        await payInstr.mutateAsync({
          id: invoice.id,
          instrument_type: payMethod === "CHECK" ? "CHECK" : "PROMISSORY_NOTE",
          serial_no: serialNo.trim(),
          issue_date: isoDate(new Date()),
          due_date: isoDate(dueDate),
          amount_minor: minor,
          ...(bankName && { bank_name: bankName }),
        });
        notifications.show({ message: "Çek/senet ile ödendi — portföye eklendi", color: "green" });
      } else {
        await pay.mutateAsync({ id: invoice.id, amount_minor: minor, payment_method: payMethod });
        notifications.show({ message: "Ödeme kaydedildi", color: "green" });
      }
      refreshLedgerCaches();
      resetPay(); onChanged?.(); onClose();
    } catch (e: any) {
      notifications.show({ message: e?.response?.data?.detail || "Hata", color: "red" });
    }
  }

  return (
    <Modal
      opened={!!invoice}
      onClose={onClose}
      size="lg"
      title={
        <Group gap="sm">
          <Text fw={700}>{invoice?.invoice_number}</Text>
          {st && <Badge color={st.color}>{st.label}</Badge>}
        </Group>
      }
    >
      {invoice && (
        <Stack gap="md">
          <Group gap="xl" justify="space-between">
            <Group gap="xl">
              <Box label="Tarih" value={formatDate(invoice.invoice_date)} />
              <Box label="Vade" value={invoice.due_date ? formatDate(invoice.due_date) : "—"} />
              <Box label="Toplam" value={<MoneyDisplay minor={invoice.total_minor} fw={600} />} />
              <Box label="Kalan" value={<MoneyDisplay minor={remaining} fw={600} />} />
            </Group>
            <Group gap="xs" wrap="nowrap">
              {invoice.has_attachment && (
                <Button
                  variant="light"
                  leftSection={<IconPaperclip size={16} />}
                  onClick={downloadAttachment}
                  loading={downloading}
                >
                  Eki İndir
                </Button>
              )}
              {/* Gerçek fatura belgesini sonradan ekle/değiştir (MinIO). */}
              <FileInput
                placeholder={invoice.has_attachment ? "Belgeyi değiştir" : "Fatura belgesi ekle"}
                leftSection={<IconUpload size={16} />}
                accept="application/pdf,image/jpeg,image/png,image/webp"
                value={null}
                onChange={uploadAttachment}
                disabled={uploading}
                clearable={false}
                w={220}
              />
            </Group>
          </Group>

          <Table striped withTableBorder>
            <Table.Thead>
              <Table.Tr>
                <Table.Th>Açıklama</Table.Th>
                <Table.Th ta="right">Miktar</Table.Th>
                <Table.Th ta="right">Birim Fiyat</Table.Th>
                <Table.Th ta="right">KDV %</Table.Th>
                <Table.Th ta="right">Tutar</Table.Th>
              </Table.Tr>
            </Table.Thead>
            <Table.Tbody>
              {lines.map((l) => (
                <Table.Tr key={l.id}>
                  <Table.Td>{l.description || "—"}</Table.Td>
                  <Table.Td ta="right">{l.quantity}</Table.Td>
                  <Table.Td ta="right"><MoneyDisplay minor={l.unit_price_minor} /></Table.Td>
                  <Table.Td ta="right">{l.tax_rate_percent}</Table.Td>
                  <Table.Td ta="right" fw={600}><MoneyDisplay minor={l.line_total_minor} /></Table.Td>
                </Table.Tr>
              ))}
            </Table.Tbody>
            <Table.Tfoot>
              <Table.Tr>
                <Table.Th colSpan={4} ta="right">Ara Toplam</Table.Th>
                <Table.Th ta="right"><MoneyDisplay minor={invoice.subtotal_minor} /></Table.Th>
              </Table.Tr>
              <Table.Tr>
                <Table.Th colSpan={4} ta="right">KDV</Table.Th>
                <Table.Th ta="right"><MoneyDisplay minor={invoice.tax_minor} /></Table.Th>
              </Table.Tr>
              <Table.Tr>
                <Table.Th colSpan={4} ta="right">Genel Toplam</Table.Th>
                <Table.Th ta="right"><MoneyDisplay minor={invoice.total_minor} fw={700} /></Table.Th>
              </Table.Tr>
            </Table.Tfoot>
          </Table>

          {returnOpen ? (
            <Stack gap="sm">
              <Text fw={600} size="sm">Kısmi İade (tedarikçiye mal iadesi)</Text>
              <Text size="xs" c="dimmed">
                Yalnız mal kabulü yapılmış (irsaliyeye bağlı) kalemler iade edilebilir.
                İade onaylanınca stok düşer ve tedarikçi borcu azalır.
              </Text>
              {returnableLines.length === 0 ? (
                <Text size="sm" c="orange">
                  Bu faturanın hiçbir kalemi mal kabulüne (GR) bağlı değil — iade edilemez.
                </Text>
              ) : (
                returnableLines.map((l) => (
                  <Group key={l.id} grow align="flex-end">
                    <Text size="sm">{l.description || "—"} (alındı: {l.quantity})</Text>
                    <NumberInput
                      label="İade miktarı"
                      value={returnQty[l.id] ?? 0}
                      min={0}
                      max={Number(l.quantity)}
                      decimalScale={3}
                      onChange={(v) => setReturnQty((q) => ({ ...q, [l.id]: v }))}
                      w={140}
                    />
                  </Group>
                ))
              )}
              <TextInput
                label="İade Sebebi"
                placeholder="Kusurlu / fazla teslimat vb."
                value={returnReason}
                onChange={(e) => setReturnReason(e.currentTarget.value)}
              />
              <Group justify="flex-end">
                <Button variant="default" onClick={() => { setReturnOpen(false); setReturnQty({}); }}>Vazgeç</Button>
                <Button color="orange" onClick={doReturn} loading={returnSaving}
                  disabled={returnableLines.length === 0}>
                  İadeyi Onayla
                </Button>
              </Group>
            </Stack>
          ) : !payOpen ? (
            <Group justify="flex-end">
              {invoice.status === "DRAFT" && (
                <Button onClick={doPost} loading={post.isPending}>İşle (cariye düş)</Button>
              )}
              {["POSTED", "PARTIALLY_PAID"].includes(invoice.status) && (
                <Button color="green" onClick={() => { setPayAmount(suggestedPayMinor / 100); setPayOpen(true); }}>
                  Öde
                </Button>
              )}
              {["POSTED", "PARTIALLY_PAID", "PAID", "PARTIALLY_REFUNDED"].includes(invoice.status) && (
                <Button variant="light" color="orange" onClick={() => setReturnOpen(true)}>
                  Kısmi İade
                </Button>
              )}
            </Group>
          ) : (
            <Stack gap="sm">
              <Group grow>
                <NumberInput label="Ödeme Tutarı (₺)" value={payAmount} min={0} decimalScale={2}
                  onChange={setPayAmount} />
                <Select label="Yöntem" value={payMethod} onChange={(v) => setPayMethod(v || "BANK_TRANSFER")}
                  data={[
                    { value: "BANK_TRANSFER", label: "Banka Havalesi/EFT" },
                    { value: "CASH", label: "Nakit" },
                    { value: "CHECK", label: "Çek" },
                    { value: "NOTE", label: "Senet" },
                    { value: "CARD", label: "Kredi Kartı" },
                  ]} />
              </Group>
              {/* Çek/senet seçilince: seri no + vade (+ banka, çekte). Çek faturayı
                  kapatır, Çek/Senet sekmesinde görünür; vade gelince tahsil/karşılıksız. */}
              {isInstrument && (
                <>
                  <Group grow>
                    <TextInput label="Seri No" required value={serialNo}
                      onChange={(e) => setSerialNo(e.currentTarget.value)} />
                    <DateInput label="Vade Tarihi" required value={dueDate}
                      onChange={(d) => setDueDate(d as Date | null)} valueFormat="DD.MM.YYYY" />
                  </Group>
                  {payMethod === "CHECK" && (
                    <TextInput label="Banka" placeholder="Banka adı (opsiyonel)"
                      value={bankName} onChange={(e) => setBankName(e.currentTarget.value)} />
                  )}
                  <Text size="xs" c="dimmed">
                    Çek/senet portföye eklenir; vade/tahsil takibi "Çek/Senet" sekmesinden.
                    Karşılıksız çıkarsa fatura yeniden açılır.
                  </Text>
                </>
              )}
              <Group justify="flex-end">
                <Button variant="default" onClick={resetPay}>Vazgeç</Button>
                <Button color="green" onClick={doPay} loading={pay.isPending || payInstr.isPending}>
                  {isInstrument ? "Çek/Senet ile Öde" : "Ödemeyi Kaydet"}
                </Button>
              </Group>
            </Stack>
          )}
        </Stack>
      )}
    </Modal>
  );
}

function Box({ label, value }: { label: string; value: ReactNode }) {
  return (
    <div>
      <Text size="xs" c="dimmed">{label}</Text>
      <Text fw={500} component="div">{value}</Text>
    </div>
  );
}
